/**
 * Runs tab — a filterable table of per-run token/cost rollups over a date window
 * (from `useRuns`) with a click-through detail view rendering the run's labels
 * and a per-turn timeline (from `useTurns`).
 *
 * READ-ONLY: the console never mutates. All state is local (`useState`); there is
 * no router. The window/platform/label filters are applied on change and survive
 * a drill-in → Back round-trip (the detail view is a separate `openRun` state).
 *
 * The detail view also mounts <LogsPanel/> — the per-run general-log drill-in.
 */

import { useState } from 'react';
import { useRuns, useTurns, type Run } from '../lib/queries';
import LogsPanel from '../components/LogsPanel';

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests — no DOM needed).
// ---------------------------------------------------------------------------

/** `YYYY-MM-DD` for `days` days before `end` (default: now), computed in UTC so
 *  it is stable regardless of the host timezone. `isoDaysAgo(0)` is today. */
export function isoDaysAgo(days: number, end: Date = new Date()): string {
  const d = new Date(end.getTime() - days * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/** Parse a space-separated `k=v` filter string into a label map. Tokens without
 *  exactly one non-empty key and value are ignored (malformed → dropped). */
export function parseLabelFilters(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const token of text.trim().split(/\s+/)) {
    if (!token) continue;
    const eq = token.indexOf('=');
    if (eq <= 0 || eq === token.length - 1) continue;
    const key = token.slice(0, eq);
    const value = token.slice(eq + 1);
    if (key && value) out[key] = value;
  }
  return out;
}

/** Newest-first: descending `run_date`, tie-broken by descending `last_seen`
 *  (missing `last_seen` sorts as ''). Returns a new array — never mutates. */
export function sortRuns(runs: Run[]): Run[] {
  return runs.slice().sort((a, b) => {
    if (a.run_date !== b.run_date) return a.run_date < b.run_date ? 1 : -1;
    const la = a.last_seen ?? '';
    const lb = b.last_seen ?? '';
    if (la === lb) return 0;
    return la < lb ? 1 : -1;
  });
}

/** Group-formatted token count, or an em-dash for null/undefined. */
export function fmtTokens(n?: number): string {
  return n === null || n === undefined ? '—' : n.toLocaleString();
}

/** Fixed 4-dp USD cost, e.g. `$0.1234`. */
function fmtCost(n: number): string {
  return `$${n.toFixed(4)}`;
}

// ---------------------------------------------------------------------------
// Run detail — meta, label pills, and the per-turn timeline.
// ---------------------------------------------------------------------------

function RunDetail({ run, onBack }: { run: Run; onBack: () => void }) {
  const { data, isLoading, isError, error, refetch } = useTurns(
    run.platform,
    run.session_id ?? '',
  );
  const turns = data?.events ?? [];
  const maxCost = turns.reduce((m, t) => Math.max(m, t.cost_usd), 0);
  const labels = Object.entries(run.labels ?? {});

  return (
    <section className="run-detail" aria-label={`Run ${run.session_id ?? run.platform}`}>
      <button type="button" className="btn" onClick={onBack}>
        ← Back to runs
      </button>

      <dl className="run-detail-meta">
        <div className="meta-row">
          <dt>platform</dt>
          <dd>{run.platform}</dd>
        </div>
        <div className="meta-row">
          <dt>session</dt>
          <dd className="mono">{run.session_id ?? '—'}</dd>
        </div>
        <div className="meta-row">
          <dt>run date</dt>
          <dd>{run.run_date}</dd>
        </div>
        <div className="meta-row">
          <dt>models</dt>
          <dd>{run.models.length ? run.models.join(', ') : '—'}</dd>
        </div>
        <div className="meta-row">
          <dt>turns</dt>
          <dd>{run.turn_count}</dd>
        </div>
        <div className="meta-row">
          <dt>cost</dt>
          <dd className="mono">{fmtCost(run.cost_usd)}</dd>
        </div>
        {run.error_count !== undefined && run.error_count > 0 && (
          <div className="meta-row">
            <dt>errors</dt>
            <dd>
              <span className="pill pill-danger">{run.error_count} errors</span>
            </dd>
          </div>
        )}
      </dl>

      {labels.length > 0 && (
        <>
          <h3 className="section-title">Labels</h3>
          <div className="run-detail-labels">
            {labels.map(([k, v]) => (
              <span key={k} className="pill">
                {k}={v}
              </span>
            ))}
          </div>
        </>
      )}

      <h3 className="section-title">Turns</h3>
      {isLoading ? (
        <p className="note">Loading turns…</p>
      ) : isError ? (
        <p className="error" role="alert">
          Failed to load turns: {error instanceof Error ? error.message : 'unknown error'}{' '}
          <button type="button" className="btn" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      ) : turns.length === 0 ? (
        <p className="note">No turns recorded for this run.</p>
      ) : (
        <table className="detail-table">
          <thead>
            <tr>
              <th>ts</th>
              <th>model</th>
              <th className="detail-num">input</th>
              <th className="detail-num">output</th>
              <th className="detail-num">cache r/w</th>
              <th className="detail-num">cost</th>
              <th>cost bar</th>
            </tr>
          </thead>
          <tbody>
            {turns.map((t) => (
              <tr key={t.sk}>
                <td className="mono">{t.ts_iso}</td>
                <td>{t.model}</td>
                <td className="detail-num">{fmtTokens(t.input_tokens)}</td>
                <td className="detail-num">{fmtTokens(t.output_tokens)}</td>
                <td className="detail-num">
                  {fmtTokens(t.cache_read_tokens)} / {fmtTokens(t.cache_creation_tokens)}
                </td>
                <td className="detail-num mono">{fmtCost(t.cost_usd)}</td>
                <td>
                  <span
                    className="turn-bar"
                    style={{ width: maxCost > 0 ? `${(t.cost_usd / maxCost) * 100}%` : '0%' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <LogsPanel platform={run.platform} sessionId={run.session_id ?? ''} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// The tab.
// ---------------------------------------------------------------------------

export default function RunsTab() {
  const [from, setFrom] = useState(() => isoDaysAgo(7));
  const [to, setTo] = useState(() => isoDaysAgo(0));
  const [platform, setPlatform] = useState('');
  const [labelText, setLabelText] = useState('');
  const [openRun, setOpenRun] = useState<Run | null>(null);

  const { data, isLoading, isError, error, refetch } = useRuns(
    from,
    to,
    platform || undefined,
    parseLabelFilters(labelText),
  );

  const controls = (
    <div className="card runs-controls">
      <label className="runs-control">
        <span className="runs-control-label">From</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
      </label>
      <label className="runs-control">
        <span className="runs-control-label">To</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </label>
      <label className="runs-control">
        <span className="runs-control-label">Platform</span>
        <input
          type="text"
          placeholder="any"
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
        />
      </label>
      <label className="runs-control">
        <span className="runs-control-label">Labels</span>
        <input
          type="text"
          placeholder="team=alpha env=prod"
          value={labelText}
          onChange={(e) => setLabelText(e.target.value)}
        />
      </label>
    </div>
  );

  if (openRun) {
    return (
      <section className="runs-tab" aria-label="Runs">
        {controls}
        <RunDetail run={openRun} onBack={() => setOpenRun(null)} />
      </section>
    );
  }

  const runs = sortRuns(data?.runs ?? []);

  return (
    <section className="runs-tab" aria-label="Runs">
      {controls}

      {isLoading ? (
        <p className="note">Loading runs…</p>
      ) : isError ? (
        <p className="error" role="alert">
          Failed to load runs: {error instanceof Error ? error.message : 'unknown error'}{' '}
          <button type="button" className="btn" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      ) : runs.length === 0 ? (
        <p className="note">No runs in this window.</p>
      ) : (
        <table className="detail-table runs-table">
          <thead>
            <tr>
              <th>platform</th>
              <th>session</th>
              <th>date</th>
              <th>models</th>
              <th className="detail-num">turns</th>
              <th className="detail-num">input</th>
              <th className="detail-num">output</th>
              <th className="detail-num">cost</th>
              <th>flags</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => {
              const clickable = Boolean(run.session_id);
              return (
                <tr
                  key={`${run.platform}#${run.session_id ?? run.run_date}`}
                  data-session-id={run.session_id ?? ''}
                  className={clickable ? 'runs-row' : undefined}
                  style={clickable ? { cursor: 'pointer' } : undefined}
                  onClick={clickable ? () => setOpenRun(run) : undefined}
                >
                  <td>{run.platform}</td>
                  <td className="mono">{run.session_id ?? '—'}</td>
                  <td>{run.run_date}</td>
                  <td>{run.models.length ? run.models.join(', ') : '—'}</td>
                  <td className="detail-num">{run.turn_count}</td>
                  <td className="detail-num">{fmtTokens(run.input_tokens)}</td>
                  <td className="detail-num">{fmtTokens(run.output_tokens)}</td>
                  <td className="detail-num mono">{fmtCost(run.cost_usd)}</td>
                  <td>
                    {run.error_count !== undefined && run.error_count > 0 && (
                      <span className="pill pill-danger">{run.error_count} errors</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
